import { useMemo } from "react";
import { BlockMath, InlineMath } from "react-katex";

/**
 * Renders text that may contain LaTeX delimiters $...$ (inline) and $$...$$ (block).
 * Falls back to a red-code fragment when a piece fails to parse.
 */
export default function MathText({ children }) {
  const value = String(children || "");
  const parts = useMemo(() => {
    const out = [];
    const blockRe = /\$\$([\s\S]+?)\$\$/g;
    let last = 0;
    let m;
    while ((m = blockRe.exec(value)) !== null) {
      if (m.index > last) out.push({ kind: "text", value: value.slice(last, m.index) });
      out.push({ kind: "block", value: m[1] });
      last = m.index + m[0].length;
    }
    if (last < value.length) out.push({ kind: "text", value: value.slice(last) });

    const expanded = [];
    const inlineRe = /\$([^$\n]+?)\$/g;
    for (const seg of out) {
      if (seg.kind !== "text") { expanded.push(seg); continue; }
      let l = 0; let mm;
      while ((mm = inlineRe.exec(seg.value)) !== null) {
        if (mm.index > l) expanded.push({ kind: "text", value: seg.value.slice(l, mm.index) });
        expanded.push({ kind: "inline", value: mm[1] });
        l = mm.index + mm[0].length;
      }
      if (l < seg.value.length) expanded.push({ kind: "text", value: seg.value.slice(l) });
    }
    return expanded.map((p, i) => ({ ...p, _k: `${p.kind}-${i}` }));
  }, [value]);

  return (
    <span>
      {parts.map((p) => {
        if (p.kind === "text") return <span key={p._k}>{p.value}</span>;
        try {
          return p.kind === "block"
            ? <BlockMath key={p._k} math={p.value} />
            : <InlineMath key={p._k} math={p.value} />;
        } catch {
          return <code key={p._k} className="text-rose-600">{p.value}</code>;
        }
      })}
    </span>
  );
}
